import type { Annotation, DrawingDocument } from '../model';

export type AnnotationStyle = NonNullable<Extract<Annotation, { kind: 'shape' }>['style']>;
export type AnnotationStyleKey = keyof AnnotationStyle;

export type StyledAnnotation = Extract<Annotation, { kind: 'shape' | 'curved-arrow' | 'reaction-arrow' | 'text' }>;

export function isStyledAnnotation(annotation: Annotation): annotation is StyledAnnotation {
  return (
    annotation.kind === 'shape' ||
    annotation.kind === 'curved-arrow' ||
    annotation.kind === 'reaction-arrow' ||
    annotation.kind === 'text'
  );
}

const LINE_KEYS: readonly AnnotationStyleKey[] = ['color', 'lineStyle', 'lineWidth', 'opacity'];
const CLOSED_SHAPE_KEYS: readonly AnnotationStyleKey[] = [...LINE_KEYS, 'fill', 'shadow', 'outline', 'background'];

/**
 * #859 (#842 S1 / C4) -- the style properties an annotation accepts: a closed shape takes fill,
 * line and opacity; a freehand path and an arrow take line and opacity; a text box takes its
 * colour and opacity (its background and border come with padding, #843 step 3). #866 (S5) --
 * shapes and paths take a shadow, rectangles rounded corners too; arrows and text take neither.
 */
export function applicableStyleKeys(annotation: Annotation): readonly AnnotationStyleKey[] {
  if (annotation.kind === 'shape') {
    if (annotation.shape === 'path') return [...LINE_KEYS, 'shadow'];
    return annotation.shape === 'rect' ? [...CLOSED_SHAPE_KEYS, 'cornerRadius'] : CLOSED_SHAPE_KEYS;
  }
  if (annotation.kind === 'curved-arrow' || annotation.kind === 'reaction-arrow') return LINE_KEYS;
  if (annotation.kind === 'text') return ['color', 'opacity'];
  return [];
}

/** Applies `style` to the selected annotations, each taking only the properties it accepts. A
 *  property set to `undefined` is removed (back to the standard look). */
export function styleAnnotations(
  document: DrawingDocument,
  ids: readonly string[],
  style: Partial<Record<AnnotationStyleKey, AnnotationStyle[AnnotationStyleKey] | undefined>>
): DrawingDocument {
  let changed = false;
  const annotations = document.annotations.map((annotation) => {
    if (!ids.includes(annotation.id) || !isStyledAnnotation(annotation)) return annotation;
    const accepted = applicableStyleKeys(annotation);
    const next: Record<string, unknown> = { ...annotation.style };
    for (const [key, value] of Object.entries(style)) {
      if (!accepted.includes(key as AnnotationStyleKey)) continue;
      if (value === undefined) delete next[key];
      else next[key] = value;
    }
    if (JSON.stringify(next) === JSON.stringify(annotation.style ?? {})) return annotation;
    changed = true;
    const { style: _previous, ...rest } = annotation;
    return (Object.keys(next).length > 0 ? { ...rest, style: next } : rest) as Annotation;
  });
  return changed ? { ...document, annotations } : document;
}

/** The part of `style` that `annotation` accepts -- for stamping new-object defaults (#859). */
export function applicableStyle(annotation: Annotation, style: AnnotationStyle): AnnotationStyle {
  const accepted = applicableStyleKeys(annotation);
  return Object.fromEntries(
    Object.entries(style).filter(([key]) => accepted.includes(key as AnnotationStyleKey))
  ) as AnnotationStyle;
}

const ALL_KEYS: readonly AnnotationStyleKey[] = [
  'color',
  'lineStyle',
  'fill',
  'lineWidth',
  'opacity',
  'cornerRadius',
  'shadow',
  'outline',
  'background'
];

/**
 * #861 (#842 S8) -- Paste style: the target takes the copied style exactly, so a property the
 * source left at the standard look is reset on the target too. `styleAnnotations` then applies
 * only what each target accepts (no fill onto an arrow, colour and opacity only onto text).
 */
export function pasteStylePatch(
  copied: AnnotationStyle
): Partial<Record<AnnotationStyleKey, AnnotationStyle[AnnotationStyleKey] | undefined>> {
  return Object.fromEntries(ALL_KEYS.map((key) => [key, copied[key]]));
}
